import { useCallback, useEffect, useState } from "react";
import { useSaveBarSafeNavigate } from "../../../hooks/useSaveBarSafeNavigate.js";
import { ArrowLeftMinor } from "@shopify/polaris-icons";
import { SubscriptionPlan } from "@prisma/client";
import { closeModal } from "../../../store/features/modal.js";
import { Modal, Stack, Text, TextContainer, Button } from "@shopify/polaris";
import { OPEN_MODAL_UPSELL } from "../../../constants/modalTypes.js";
import { billing_finances, ok_icon } from "../../../assets/index.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux.js";
import { useAppQuery } from "../../../hooks/useAppQuery.js";
import { SUBSCRIPTION_PLAN_ENTERPRISE } from "../../../../constants/subscriptionPlans.js";
import { makeId } from "../../../utils/makeId.js";
import { STOCKHIDE_CONTACT_US_LINK } from "../../../../constants/links.js";
import { EVERY_30_DAYS } from "../../../../constants/subscriptionInterval.js";

import styles from "./UpsellModal.module.css";
import { useAppMutation } from "../../../hooks/useAppMutation.js";

interface ISubscriptionPlans {
  customPlan: SubscriptionPlan | null,
  defaultSubscriptionPlans: SubscriptionPlan[]
}

const enterprisePlan = {
  id: makeId(20),
  name: SUBSCRIPTION_PLAN_ENTERPRISE,
  productsLimit: 100000,
  collectionsLimit: 5000,
  monthlyPrice: null,
  yearlyPricePerMo: null,
  crossedPrice: null,
  createdAt: new Date(),
  updatedAt: new Date(),
};

const emptyPlan = {
  id: "-1",
  name: "",
  productsLimit: 0,
  collectionsLimit: 0,
  monthlyPrice: 0,
  yearlyPricePerMo: 0,
  crossedPrice: 0,
  createdAt: new Date(),
  updatedAt: new Date(),
};

const UpsellModal = () => {
  const { isOpen, modalType, modalData } = useAppSelector((state) => state.modal);
  const [loading, setLoading] = useState(false);
  const [nextPlan, setNextPlan] = useState<SubscriptionPlan>(emptyPlan);
  const dispatch = useAppDispatch();
  const navigate = useSaveBarSafeNavigate();
  const shop = useAppSelector((state) => state.shop.data);
  const host = useAppSelector((state) => state.host.data);
  const shopifyAppSubscriptionInterval = useAppSelector((state) => state?.shop?.data?.shopifyAppSubscription?.lineItems[0]?.plan?.pricingDetails?.interval);
  const [subscriptionInterval, setSubscriptionInterval] = useState(shopifyAppSubscriptionInterval || EVERY_30_DAYS);

  const currentPlan = shop.subscriptionPlan ?? emptyPlan;

  const { isLoading: isGetPlansLoading, data } = useAppQuery<ISubscriptionPlans, Error>({
    url: "/api/app-subscription/plans",
    reactQueryOptions: {},
    fetchInit: {}
  });

  useEffect(() => {
    if (data && currentPlan) {
      const plans = [...data.defaultSubscriptionPlans, enterprisePlan];
      plans.sort((a, b) => a.productsLimit < b.productsLimit ? -1 : 1);

      if (modalData?.limitProductCount !== undefined && modalData?.limitCollectionCount !== undefined) {
        const selectedPlan = plans.find((plan) =>
          plan.productsLimit >= modalData?.limitProductCount && plan.collectionsLimit >= modalData?.limitCollectionCount
        ) ?? enterprisePlan;
        setNextPlan(selectedPlan);
      } else {
        const selectedPlan = plans.find((plan) => plan.productsLimit > currentPlan.productsLimit) ?? enterprisePlan;
        setNextPlan(selectedPlan);
      }
    }
  }, [data, modalData]);

  const { mutate, isLoading } = useAppMutation({
    url: "/api/app-subscription/create",
    reactMutationOptions: {
      onSuccess: ({ confirmUrl }) => {
        if (confirmUrl) {
          // "noopener" makes App Bridge ask the Shopify admin to open the tab, so the popup blocker doesn't stop it
          window.open(confirmUrl, modalData?.isNewContect ? "_blank" : "_top", "noopener");
        }
        setLoading(false);
        dispatch(closeModal());
      }
    }
  });

  const handleChoose = useCallback(() => {
    if (nextPlan.name === SUBSCRIPTION_PLAN_ENTERPRISE) {
      return window.open(STOCKHIDE_CONTACT_US_LINK, "_blank");
    }
    setLoading(true);
    mutate({ planId: nextPlan.id, subscriptionInterval, host });
  }, [mutate, nextPlan, subscriptionInterval]);

  const handleNavigate = useCallback(() => {
    navigate("/billing");
    dispatch(closeModal());
  }, []);

  const handleClose = useCallback(() => {
    dispatch(closeModal());
  }, []);

  return (
    <Modal
      open={isOpen && modalType === OPEN_MODAL_UPSELL}
      onClose={handleClose}
      title="You are over your plan limit"
      footer={(
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: 7 }}>
          <Button disabled={loading || isLoading} onClick={handleClose} icon={ArrowLeftMinor}>Go Back</Button>
          <Button disabled={loading || isLoading} onClick={handleNavigate}>See all plans</Button>
        </div>
      )}
      primaryAction={{
        loading,
        disabled: loading || isLoading,
        content: `Upgrade to ${nextPlan.name}`,
        onAction: handleChoose
      }}
    >
      <Modal.Section>
        <div style={{ margin: "0 auto" }}>
          <Stack vertical distribution="trailing" alignment="center">
            <div style={{ maxWidth: 400 }}>
              <Text as="h3" variant="bodyMd" alignment="center">
                You have exceeded your <strong>{currentPlan.productsLimit} Products / {currentPlan.collectionsLimit} Collections</strong> limit. Please upgrade your Stockhide plan.
              </Text>
            </div>
            <Stack>
              <div style={{ width: "230px" }}>
                <img className={styles.upsellImage} src={billing_finances} alt="upsell_modal_image" />
              </div>
              <Stack vertical spacing="tight">
                <Text as="h3" variant="headingSm">
                  GET OUR <span style={{ color: "#4CC3C1" }}>{nextPlan.name}</span> PLAN
                </Text>
                <TextContainer spacing="tight">
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <img src={ok_icon} style={{ width: 18, height: 18 }} />
                    <Text as="span" variant="bodyMd">{nextPlan.productsLimit} products</Text>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <img src={ok_icon} style={{ width: 18, height: 18 }} />
                    <Text as="span" variant="bodyMd">{nextPlan.collectionsLimit} collections</Text>
                  </div>
                </TextContainer>
                {nextPlan.name !== enterprisePlan.name ? (
                  <div className={styles.priceContainer}>
                    <div style={{ textDecoration: "line-through" }}>
                      <Text color="subdued" as="span" variant="bodyMd">${nextPlan?.crossedPrice ?? 0}/mo</Text>
                    </div>
                    <div style={{ color: "#258D8C" }}>
                      <Text as="span" variant="headingLg">${nextPlan?.monthlyPrice ?? 0}</Text>
                      <Text as="span" variant="bodyMd">/mo</Text>
                    </div>
                    <div><Text as="span" variant="bodyMd">cancel anytime!</Text></div>
                  </div>
                ) : (
                  <div className={styles.priceContainer}>
                    <div style={{ color: "#258D8C" }}>
                      <Text as="span" variant="headingLg">contact us</Text>
                    </div>
                    <div><Text as="span" variant="bodyMd">cancel anytime!</Text></div>
                  </div>
                )}
              </Stack>
            </Stack>
          </Stack>
        </div>
      </Modal.Section>
    </Modal>
  );
};

export default UpsellModal;